import clsx from "clsx";
import type { LevelUpInfo, HeroStats } from "../types";
import { deltaLabel } from "../utils/levelUp";
import { ModalBackdrop } from "./ModalBackdrop";

interface LevelUpModalProps {
  info: LevelUpInfo;
  onDismiss: () => void;
  onGoToHero: () => void;
}

const DELTA_ICONS: Partial<Record<keyof HeroStats, string>> = {
  power: "💪",
  armor: "🛡️",
  endurance: "🫀",
  hp: "❤️",
  patk: "⚔️",
  matk: "✨",
  pdef: "🔰",
  mdef: "🔮",
  crit: "💥",
};

const DELTA_ORDER: (keyof HeroStats)[] = [
  "power",
  "armor",
  "endurance",
  "hp",
  "patk",
  "matk",
  "pdef",
  "mdef",
  "crit",
];

export function LevelUpModal({ info, onDismiss, onGoToHero }: LevelUpModalProps) {
  return (
    <ModalBackdrop onClose={onDismiss} className="z-50 items-center bg-black/75 backdrop-blur-sm p-6">
      <div className="w-full max-w-sm bg-gradient-to-b from-kingdom-purple to-kingdom-dark border-2 border-gold-500/60 rounded-2xl p-6 text-center space-y-4 shadow-2xl animate-bounce-in">
        <div className="text-5xl">🎉</div>
        <div className="space-y-1">
          <div className="text-2xl font-bold font-game text-gold-300">Level Up!</div>
          <div className="text-kingdom-glow text-lg font-bold">
            Lv.{info.oldLevel}
            <span className="text-gray-500 mx-2">→</span>
            Lv.{info.newLevel}
          </div>
          {info.levelsGained > 1 && (
            <div className="text-xs text-gold-400/80">+{info.levelsGained} levels at once!</div>
          )}
        </div>

        <div className="bg-kingdom-dark/80 rounded-xl p-3 space-y-2 text-left">
          <div className="text-xs text-gray-400 text-center mb-1">Stats increased</div>
          {DELTA_ORDER.map((key) => {
            const delta = info.statDeltas[key];
            const value = info.newStats[key];
            if (delta <= 0) return null;
            const label = deltaLabel(key, info.heroClass);
            const icon = DELTA_ICONS[key] ?? "•";
            return (
              <div
                key={key}
                className="flex items-center justify-between px-2 py-1.5 rounded-lg bg-kingdom-mid/50"
              >
                <span className="text-sm text-gray-300">
                  {icon} {label}
                </span>
                <span className="text-sm font-bold tabular-nums">
                  <span className="text-white">{value}</span>
                  <span className="text-emerald-400 ml-2">+{delta}</span>
                </span>
              </div>
            );
          })}
        </div>

        {info.skillPointsGained > 0 && (
          <div className="text-sm text-kingdom-glow font-medium">
            🌳 +{info.skillPointsGained} skill point{info.skillPointsGained > 1 ? "s" : ""} — upgrade your skills!
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <button
            onClick={onDismiss}
            className="flex-1 py-3 rounded-xl border border-kingdom-light text-gray-300 font-bold active:scale-95 transition-transform"
          >
            OK
          </button>
          <button
            onClick={onGoToHero}
            className={clsx(
              "flex-1 py-3 rounded-xl font-bold text-black active:scale-95 transition-transform",
              "bg-gradient-to-r from-gold-500 to-gold-600",
            )}
          >
            Hero
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}
